import {ApplicationRef, Component, ElementRef, Inject, NgZone, OnInit, ViewChild} from '@angular/core';
import {RestApiService} from '../core/services/rest-api.service';
import {RuntimeCacheService} from '../core/services/runtime-cache.service';
import {L10N_LOCALE, L10nLocale, L10nTranslationService} from 'angular-l10n';
import {Breakpoints} from "@angular/cdk/layout";
import {ComboboxItem, MenuItemList, NavigationStyle, YascSidenavComponent} from '@yasc/yasc-material-components';
import {FormGroup} from '@angular/forms';
import {Router} from '@angular/router';
import {MENU_ITEMS} from './menu-entries';
import {FBookingController} from '../core/controllers/fibu/fbooking.controller';
import {QuickDocumentationComponent} from './relationship-manager/quick-documentation-component/quick-documentation.component';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {HotkeyService} from '../core/services/hotkey.service';
import {TimeClockDialogComponent} from '../partial-views/dialogs/time-clock-dialog/time-clock-dialog.component';
import {TimeClockService} from '../core/services/time-clock.service';
import moment from "moment";
import {BugReportDialogComponent} from '../partial-views/dialogs/bug-report-dialog-component/bug-report-dialog.component';
import {NotificationService} from '../core/services/notification-service';
import {BugCreateReq} from '../core/contracts/v0/core/bug-create-req';
import {take} from 'rxjs';
import * as htmlToImage from 'html-to-image';
import {PermissionList} from '../core/permissions/permission-list';

@Component({
    selector: 'cp-apps',
    templateUrl: './apps.component.html',
    styleUrls: ['./apps.component.scss'],
    standalone: false
})
export class AppsComponent implements OnInit {
    dialogRef?: MatDialogRef<QuickDocumentationComponent | TimeClockDialogComponent | BugReportDialogComponent, any> | null;

    @ViewChild('sidenavMaterial', {static: false}) sidenavContainer!: YascSidenavComponent
    @ViewChild('app-root', {static: false}) box!: ElementRef<HTMLDivElement>;

    menuItems: MenuItemList[] = MENU_ITEMS;


    constructor(
        public restApi: RestApiService,
        public runtimeCache: RuntimeCacheService,
        public translation: L10nTranslationService,
        private shortcut: HotkeyService,
        private dialog: MatDialog,
        @Inject(L10N_LOCALE) public locale: L10nLocale,
        protected bookingController: FBookingController,
        private router: Router,
        public timeClock: TimeClockService,
        private notification: NotificationService,
        private ngZone: NgZone,
    ) {
    }

    ngOnInit(): void {
        // this.runtimeCache.loadPermissions()

        this.restApi.v0.misc.getSupportedCultures().subscribe({
            next: (val) => {
                this.runtimeCache.supportedCultures = val.data;
            },
        })
        if (this.runtimeCache.hasPermission(PermissionList.AdminRoleSearch)){
            this.restApi.v0.administration.roles.search().subscribe((val) => {
                this.runtimeCache.allRights = val.data;
            })
        }


        this.shortcut.addShortcut({keys: 'control.m'}).subscribe(() => this.openQuickInput())
        this.shortcut.addShortcut({keys: 'control.o'}).subscribe(() => this.openTimeClock())
        this.shortcut.addShortcut({keys: 'control.b'}).subscribe(() => this.openBugTicket())

        for (let i = 2023; i < moment().year() + 1; i++) {
            this.financialYears.push(new ComboboxItem(i.toString(), i))
        }

        this.notification.syncFromServer().then()

    }

    protected readonly break = Breakpoints;

    formGroup: FormGroup = new FormGroup<any>({});
    // TODO: fill list from first deploy year to current year
    financialYears: ComboboxItem[] = [];

    monthNames: ComboboxItem[] = Array.from({length: 12}, (_, i) =>
        new Date(0, i).toLocaleString(this.locale.language, {month: 'long'})
    ).map(((e, i) => new ComboboxItem(e, i + 1)));

    fibuModuleActive() {
        return this.router.url.startsWith("/fibu/journal") ||
            this.router.url.startsWith("/finances");
    }

    openShortcut(value: string) {
        switch (value) {
            case 'quickInput':
                this.openQuickInput()
                break;
            case 'timeClock':
                this.openTimeClock()
                break;
            case 'reportBug':
                this.openBugTicket()
                break;
            default:
                break;
        }
    }

    openQuickInput() {
        if (this.dialogRef) return;
        this.dialogRef = this.dialog.open(QuickDocumentationComponent);
        this.dialogRef?.afterClosed().subscribe(() => {
            this.dialogRef = null;
        })
    }

    openTimeClock() {
        if (this.dialogRef) return;
        this.dialogRef = this.dialog.open(TimeClockDialogComponent)
        this.dialogRef?.afterClosed().subscribe(() => this.dialogRef = null);
    }

    openBugTicket(): void {
        this.runtimeCache.ongoingBugScreenshot = true;

        if (this.dialogRef) return;

        const node = this.box?.nativeElement ?? document.getElementById('app-root') as HTMLElement | null;
        if (!node) {
            console.error('app-root not found');
            return;
        }

        htmlToImage.toPng(node, {
            cacheBust: true,
            pixelRatio: 1,
            filter: (domNode: HTMLElement) => {
                if (domNode instanceof HTMLImageElement) {
                    const src = domNode.currentSrc || domNode.src || '';

                    if (src.startsWith('blob:')) {
                        console.warn('Skipping blob image:', src);
                        return false;
                    }
                }

                return true;
            }
        })
            .then((img) => {
                this.runtimeCache.ongoingBugScreenshot = false;
                this.openBugDialog(img)
            })
            .catch((err) => {
                this.runtimeCache.ongoingBugScreenshot = false;
                console.error('Screenshot failed, opening bug dialog without screenshot:', err);
                this.openBugDialog(null);
            }).finally(() => {
        });
    }

    private openBugDialog(screenshot: string | null): void {
        this.dialogRef = this.dialog.open(BugReportDialogComponent, {
            minWidth: '60vw',
            data: {
                route: this.router.routerState.snapshot.url,
                screenshot
            }
        });

        this.dialogRef.afterClosed().pipe(take(1)).subscribe((model: BugCreateReq) => {
            if (model) {
                this.restApi.v0.tickets.createTicket(model).subscribe({
                    next: () => this.notification.success('bugTicketCreationSucceeded'),
                    error: () => this.notification.error('bugTicketCreationFailed')
                });
            }
            this.dialogRef = null;
        });
    }


    protected readonly NavigationStyle = NavigationStyle;
}

export function numberWithCommas(x: any, lang: string = 'de-DE') {
    let thousandSeparator = '.'
    let decimalSeparator = ','
    switch (lang) {
        case 'de-DE':
            thousandSeparator = '.';
            decimalSeparator = ',';
            break;
        case 'en-US':
            thousandSeparator = ',';
            decimalSeparator = '.';
            break;
        default:
            break;
    }

    return x.toString().replace('.', decimalSeparator).replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
}
